import type { DataPointSnapResult, SnapStrategyContext } from '../types';

import { BaseSnapStrategy } from './BaseSnapStrategy';

/**
 * Snap strategy for band scales.
 *
 * Uses band-based snapping:
 * - Snaps to items when cursor is anywhere within the band's width
 * - Returns center of band as snap position
 * - Crosshair visibility is configurable
 *
 * Supports both numeric x-values (timestamps) and string x-values (categories).
 */
export class BandSnapStrategy extends BaseSnapStrategy {
  private readonly showCrosshairOnDataPoints: boolean;

  constructor(bandwidth: number, showCrosshairOnDataPoints: boolean = false) {
    super(bandwidth);
    this.showCrosshairOnDataPoints = showCrosshairOnDataPoints;
  }

  findClosestDataPoint(context: SnapStrategyContext): DataPointSnapResult | null {
    const { cursorSvgX, chartData, xDataKey, xScale, snapDistance } = context;

    // Track closest bar when cursor is inside (primary behavior)
    let insideBarResult: DataPointSnapResult | null = null;
    // Track closest bar by distance (fallback for gaps between bars)
    let closestByDistanceResult: DataPointSnapResult | null = null;

    for (const datum of chartData) {
      const xValue = datum[xDataKey];

      // Support both numeric and string x-values
      // - Numeric: timestamps (e.g., 1704067200000) or numbers (e.g., 1, 2, 3 for days)
      // - String: categories (e.g., "Control", "Treatment A")
      // Skip null/undefined values and boolean values (not valid for x-axis)
      if (xValue === null || xValue === undefined || typeof xValue === 'boolean' || Array.isArray(xValue)) {
        continue;
      }

      // For band scales, convert the value to appropriate format for scale lookup
      // Band scales accept string keys, so both numbers and strings work
      const scaleInput = typeof xValue === 'number' ? this.getScaleInputForValue(xValue, context) : String(xValue);

      // Cast xScale to accept string | number since band scales use string domain
      const scaledX = (xScale as (value: string | number) => number | undefined)(scaleInput);
      if (typeof scaledX !== 'number') {
        continue;
      }

      if (this.bandwidth > 0) {
        // For band scales, check if cursor is anywhere within the band's width
        const bandStart = scaledX;
        const bandEnd = scaledX + this.bandwidth;
        const bandCenter = this.getXPosition(scaledX, true);
        const distanceToCenter = Math.abs(bandCenter - cursorSvgX);

        const isWithinBand = cursorSvgX >= bandStart && cursorSvgX <= bandEnd;

        if (isWithinBand) {
          // Primary: cursor is inside a bar - use distance to center for prioritization
          if (!insideBarResult || distanceToCenter < insideBarResult.distance) {
            // Return center of band as xPosition for crosshair alignment
            // Store the original xValue (number or string) - will be handled by tooltip formatter
            insideBarResult = { xValue, xPosition: bandCenter, distance: distanceToCenter };
          }
        }

        // Fallback: track closest bar by distance (for continuous tooltip in gaps)
        if (
          distanceToCenter <= snapDistance &&
          (!closestByDistanceResult || distanceToCenter < closestByDistanceResult.distance)
        ) {
          closestByDistanceResult = { xValue, xPosition: bandCenter, distance: distanceToCenter };
        }
      }
    }

    // Return inside bar result first (primary), fall back to closest by distance
    // This makes tooltip behavior continuous like line charts
    return insideBarResult ?? closestByDistanceResult;
  }

  shouldShowCrosshair(hasDataPoint: boolean, hasNearbyReferenceLine: boolean): boolean {
    if (this.showCrosshairOnDataPoints) {
      // Show crosshair when snapped to data point or reference line
      return hasDataPoint || hasNearbyReferenceLine;
    }
    // Only show crosshair on reference lines (default for column charts)
    return hasNearbyReferenceLine;
  }
}
